import React, { useState, useEffect } from 'react';
import YieldComparison from '../components/YieldComparison';
import BowConversions from '../components/BowConversions';
import HandleConversions from '../components/HandleConversions';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {calculate as calculateCutting} from '../utils/cuttingRules';

const STATUS_COLORS = {
  draft:                    { bg:'#F2EFE7', color:'#556B2F', label:'Draft' },
  submitted:                { bg:'#EDF4FF', color:'#1E4FAD', label:'Submitted' },
  accepted:                 { bg:'#FFF3DC', color:'#8B5E0A', label:'Accepted' },
  in_conversion:            { bg:'#FFF3DC', color:'#8B5E0A', label:'In Conversion' },
  returned_for_verification:{ bg:'#FDEAEA', color:'#8F2E24', label:'Awaiting Verification' },
  verified:                 { bg:'#E8F5EB', color:'#2E6B3A', label:'Verified' },
  completed:                { bg:'#E8F5EB', color:'#155724', label:'Completed' },
  clarification_required:   { bg:'#FDEAEA', color:'#8F2E24', label:'Clarification Required' },
  variance_review:          { bg:'#FDEAEA', color:'#8F2E24', label:'Variance Review' },
  cancelled:                { bg:'#F2EFE7', color:'#8A8A8A', label:'Cancelled' },
};

function previewCut(rule,raw){try{return rule?.cutting_config?calculateCutting(rule.cutting_config,raw||0):null;}catch(e){return {quantity:0,error:e.message};}}
function rawWasteFor(mcr,body){const c=mcr?.cutting_config;if(!c)return 0;if(c.type==='benchmark')return Number(body.raw_waste_quantity||0);const denominator=c.source_inches*(c.type==='sheet'?60:1);const cuts=(Number(body.actual_output_quantity||0)+Number(body.damaged_quantity||0)+Number(body.actual_waste_quantity||0))*c.piece_width_inches*c.piece_length_inches;const offcuts=(body.cutting_offcuts||[]).reduce((sum,o)=>sum+Number(o.width_inches||0)*Number(o.length_inches||0)*Number(o.quantity||0),0);return Math.round((Number(body.actual_raw_consumed||0)-(cuts+offcuts)/denominator)*1e6)/1e6;}
function CuttingLayout({plan}){return plan?<div style={{padding:12,background:'#F2EFE7',margin:'12px 0',borderRadius:8}}><strong>Planned output: {plan.quantity} pieces</strong><p>{plan.error||plan.method}</p>{(plan.layout||[]).map((r,i)=><div key={i}>{r.lanes} {r.orientation==='row'?'rows':'lanes'} × {r.pieces_per_lane} pieces; each {r.piece_width_inches} × {r.piece_length_inches} inches</div>)}<p>Planning output for the full batch. Record actual results, raw returns and any applicable measured offcuts after cutting.</p></div>:null;}

export default function MaterialConversionRequests() {
  const { user } = useAuth();
  const isInventory = ['super_admin','admin','inventory_officer'].includes(user?.role);
  const isProduction = ['super_admin','admin','production_team_lead'].includes(user?.role);

  const [mcrs, setMcrs] = useState([]);
  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [rules, setRules] = useState([]);
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // detail view
  const [modal, setModal] = useState(null); // create modal
  const [actionModal, setActionModal] = useState(null); // action modal
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/inventory-control/mcr'),
      get('/inventory-control/skus'),
      get('/inventory-control/conversion-rules'),
      get('/job-orders?status=approved,in_progress&limit=100')
    ]).then(([m, s, r, jo]) => {
      setMcrs(m.data || []);
      setSkus((s.data || []).filter(sku => !/-STITCHED$|-HOSED$/.test(sku.sku)));
      setRules(r.data || []);
      setJobOrders(jo.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    get('/materials')
      .then(response => {
        setMaterials(
          (response.data || [])
            .filter(
              material =>
                material.is_active !== false
            )
        );
      })
      .catch(() => {
        setMaterials([]);
      });
  }, []);

  const activeMaterials =
    materials.filter(
      material =>
        material.is_active !== false
    );

  const destinationSKUs =
    skus.filter(
      sku =>
        sku.inventory_type ===
          'prepared' &&
        sku.is_active !== false &&
        sku.material_id ===
          modal?.source_material_id
    );

  const filtered = mcrs.filter(m => {
    const matchStatus = !statusFilter || m.status === statusFilter;
    const matchSearch = !search ||
      m.mcr_number?.toLowerCase().includes(search.toLowerCase()) ||
      m.source_description?.toLowerCase().includes(search.toLowerCase()) ||
      m.dest_description?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  // When rule is selected, auto-fill expected output
  const onRuleSelect = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (rule && modal) {
      const expectedOutput = previewCut(rule,modal.raw_quantity_to_issue)?.quantity ?? Math.floor(parseFloat(modal.raw_quantity_to_issue || 0) * parseFloat(rule.expected_yield || 0));
      const expectedWaste = rule.cutting_config ? 0 : expectedOutput * parseFloat(rule.allowable_waste_percent || 0) / 100;
      setModal({
        ...modal,
        conversion_rule_id: ruleId,
        source_material_id: rule.source_material_id,
        destination_sku_id: rule.destination_sku_id,
        cut_specification: rule.cut_specification || '',
        expected_output_quantity: expectedOutput.toFixed(0),
        expected_waste_quantity: expectedWaste.toFixed(0)
      });
    }
  };

  const onQtyChange = (qty) => {
    const rule = rules.find(r => r.id === modal?.conversion_rule_id);
    if (rule) {
      const expectedOutput = previewCut(rule,qty)?.quantity ?? Math.floor(parseFloat(qty || 0) * parseFloat(rule.expected_yield || 0));
      const expectedWaste = rule.cutting_config ? 0 : expectedOutput * parseFloat(rule.allowable_waste_percent || 0) / 100;
      setModal({
        ...modal,
        raw_quantity_to_issue: qty,
        expected_output_quantity: expectedOutput.toFixed(0),
        expected_waste_quantity: expectedWaste.toFixed(0)
      });
    } else {
      setModal({ ...modal, raw_quantity_to_issue: qty });
    }
  };

  const openCreate = () => {
    setError('');
    setModal({
      conversion_rule_id: '', source_material_id: '', destination_sku_id: '',
      raw_quantity_to_issue: '', expected_output_quantity: '',
      expected_waste_quantity: 0, cut_specification: '',
      required_completion_date: '', reason: '',
      related_job_order_id: '', assigned_to: ''
    });
  };

  const saveMCR = async () => {
    setError('');
    if (!modal.source_material_id || !modal.destination_sku_id || !modal.raw_quantity_to_issue || !modal.reason) {
      setError('Parent material, destination SKU, quantity and reason are required'); return;
    }
    setSaving(true);
    try {
      await post('/inventory-control/mcr', modal);
      setModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const submitMCR = async (id) => {
    if (!window.confirm('Submit this MCR? This will reserve the raw material for conversion.')) return;
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+id+'/submit', {});
      load();
      setSelected(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const acceptMCR = async (id) => {
    if (!window.confirm('Accept this MCR and take custody of the raw material for conversion?')) return;
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+id+'/accept', {});
      load();
      setSelected(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const openCompleteConversion = (mcr) => {
    setError('');
    setActionModal({
      type: 'complete',
      mcr,
      actual_raw_consumed: Number(mcr.raw_quantity_to_issue),
      actual_output_quantity: Number(mcr.expected_output_quantity),
      actual_waste_quantity: Number(mcr.expected_waste_quantity || 0),
      damaged_quantity: 0,
      remaining_raw_returned: 0,
      production_notes: '', cutting_offcuts: [], raw_reconciliation_confirmed: false
    });
  };

  const openVerify = (mcr) => {
    setError('');
    setActionModal({
      type: 'verify',
      mcr,
      inventory_verified_quantity: Number(mcr.actual_output_quantity ?? mcr.expected_output_quantity),
      variance_reason: ''
    });
  };

  const completeConversion = async () => {
    setError('');
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+actionModal.mcr.id+'/complete-conversion', {...actionModal, raw_waste_quantity:rawWasteFor(actionModal.mcr,actionModal)});
      setActionModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const verifyConversion = async () => {
    setError('');
    const verified = parseFloat(actionModal.inventory_verified_quantity);
    const expected = parseFloat(actionModal.mcr.actual_output_quantity ?? actionModal.mcr.expected_output_quantity);
    const variance = verified - expected;
    if (Math.abs(variance) > 0 && !actionModal.variance_reason) {
      setError('Please provide a reason for the variance'); return;
    }
    setSaving(true);
    try {
      await put('/inventory-control/mcr/'+actionModal.mcr.id+'/verify', actionModal);
      setActionModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // Status counts
  const counts = {};
  mcrs.forEach(m => { counts[m.status] = (counts[m.status]||0) + 1; });
  const actionRequired = mcrs.filter(m =>
    ['submitted','returned_for_verification'].includes(m.status)
  ).length;

  return (
    <div>
      <BowConversions />
      <HandleConversions />
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Material Conversion Requests</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Convert raw materials (rolls, yards) into prepared production units (pieces, sets)
          </p>
        </div>
        {isInventory && (
          <button className="btn btn-primary" onClick={openCreate}>+ New MCR</button>
        )}
      </div>

      {/* Action required alert */}
      {actionRequired > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
          padding:'10px 16px',marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
          ⚠ <strong>{actionRequired}</strong> conversion request{actionRequired!==1?'s':''} require action
        </div>
      )}

      {/* Status filter pills */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
        <button className={'btn btn-sm '+(statusFilter===''?'btn-primary':'btn-outline')}
          onClick={()=>setStatusFilter('')}>All ({mcrs.length})</button>
        {Object.entries(counts).map(([status, count]) => {
          const sc = STATUS_COLORS[status] || STATUS_COLORS.draft;
          return (
            <button key={status}
              className={'btn btn-sm '+(statusFilter===status?'btn-primary':'btn-outline')}
              onClick={()=>setStatusFilter(statusFilter===status?'':status)}
              style={statusFilter===status?{}:{borderColor:sc.color,color:sc.color}}>
              {sc.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search MCR number or material..."
          value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1}} />
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? <div className="spinner"/> : (
          <table>
            <thead><tr style={{background:'#3B2A21'}}>
              <th style={{color:'#F2EFE7'}}>MCR #</th>
              <th style={{color:'#F2EFE7'}}>From (Raw)</th>
              <th style={{color:'#F2EFE7'}}>To (Prepared)</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Raw Qty</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Expected Output</th>
              <th style={{color:'#F2EFE7'}}>Status</th>
              <th style={{color:'#F2EFE7'}}>Assigned To</th>
              <th style={{color:'#F2EFE7'}}>Date</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9">
                  <div className="empty-state">
                    <h3>No conversion requests</h3>
                    {isInventory && (
                      <button className="btn btn-primary" onClick={openCreate}>Create First MCR</button>
                    )}
                  </div>
                </td></tr>
              ) : filtered.map(m => {
                const sc = STATUS_COLORS[m.status] || STATUS_COLORS.draft;
                return (
                  <tr key={m.id}>
                    <td style={{fontWeight:700,fontSize:12,fontFamily:'monospace',color:'#3B2A21'}}>
                      {m.mcr_number}
                    </td>
                    <td>
                      <div style={{fontSize:12,fontWeight:600}}>{m.source_description}</div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>{m.source_sku}</div>
                    </td>
                    <td>
                      <div style={{fontSize:12,fontWeight:600}}>{m.dest_description}</div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>{m.dest_sku}</div>
                    </td>
                    <td className="amt">
                      {Number(m.raw_quantity_to_issue).toLocaleString()} {m.source_unit}
                    </td>
                    <td className="amt">
                      {Number(m.expected_output_quantity).toLocaleString()} {m.dest_unit}<br/><small>Reported: {m.actual_output_quantity==null?'pending':Number(m.actual_output_quantity)}; verified: {m.inventory_verified_quantity==null?'pending':Number(m.inventory_verified_quantity)}</small>
                    </td>
                    <td>
                      <span style={{...sc,fontSize:9,fontWeight:700,padding:'3px 10px',
                        borderRadius:20,textTransform:'uppercase',whiteSpace:'nowrap'}}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={{fontSize:11,color:'#8A8A8A'}}>{m.assigned_to_name||'—'}</td>
                    <td style={{fontSize:11,color:'#8A8A8A'}}>
                      {new Date(m.created_at).toLocaleDateString('en-NG')}
                    </td>
                    <td>
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-ghost btn-sm"
                          onClick={async()=>{ const r=await get('/inventory-control/mcr/'+m.id); setSelected(r.data); }}>
                          View
                        </button>
                        {isInventory && m.status==='draft' && (
                          <button className="btn btn-primary btn-sm" onClick={()=>submitMCR(m.id)}>
                            Submit
                          </button>
                        )}
                        {isProduction && m.status==='submitted' && (
                          <button className="btn btn-primary btn-sm"
                            style={{background:'#8B5E0A',border:'none'}} onClick={()=>acceptMCR(m.id)}>
                            Accept
                          </button>
                        )}
                        {isProduction && m.status==='accepted' && (
                          <button className="btn btn-primary btn-sm"
                            style={{background:'#556B2F',border:'none'}} onClick={()=>openCompleteConversion(m)}>
                            Complete
                          </button>
                        )}
                        {isInventory && m.status==='returned_for_verification' && (
                          <button className="btn btn-primary btn-sm"
                            style={{background:'#2E6B3A',border:'none'}} onClick={()=>openVerify(m)}>
                            Verify
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelected(null)}>
          <div className="modal" style={{maxWidth:620}}>
            <div className="modal-hdr">
              <span className="modal-title">{selected.mcr_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                {[
                  ['Status', <span style={{...STATUS_COLORS[selected.status],fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:20}}>{STATUS_COLORS[selected.status]?.label}</span>],
                  ['Created By', selected.created_by_name],
                  ['Source', `${selected.source_description} (${selected.source_sku})`],
                  ['Destination', `${selected.dest_description} (${selected.dest_sku})`],
                  ['Raw Qty to Issue', `${Number(selected.raw_quantity_to_issue).toLocaleString()} ${selected.source_unit}`],
                  ['Expected Output', `${Number(selected.expected_output_quantity).toLocaleString()} ${selected.dest_unit}`],
                  ['Expected Waste', selected.expected_waste_quantity > 0 ? Number(selected.expected_waste_quantity).toLocaleString() : '—'],
                  ['Cut Specification', selected.cut_specification||'—'],
                  ['Required By', selected.required_completion_date||'—'],
                  ['Reason', selected.reason],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>{label}</div>
                    <div style={{fontSize:13,color:'#3B2A21',fontWeight:500}}>{value}</div>
                  </div>
                ))}
              </div>

              <CuttingLayout plan={selected.cutting_plan} />
              <YieldComparison record={selected} type="mcr" onRefresh={()=>{setSelected(null);load();}}/>
              {selected.cutting_config && selected.raw_waste_quantity!==null && <div style={{padding:12,marginBottom:12}}>
                <strong>Raw waste: {Number(selected.raw_waste_quantity)} {selected.cutting_config.source_unit}</strong>
                {(selected.cutting_offcuts||[]).map((o,i)=><p key={i}>Reusable offcut: {o.quantity} × {o.width_inches} × {o.length_inches} inches; {selected.status==='completed'?'verified and recorded':'awaiting verification'}</p>)}
              </div>}
              {/* Conversion results if completed */}
              {selected.actual_output_quantity != null && (
                <div style={{background:'#E8F5EB',borderRadius:8,padding:14,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:12,color:'#2E6B3A',marginBottom:10}}>Conversion Results</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                    {[
                      ['Raw Consumed', `${Number(selected.actual_raw_consumed||0).toLocaleString()} ${selected.source_unit}`],
                      ['Output Produced', `${Number(selected.actual_output_quantity||0).toLocaleString()} ${selected.dest_unit}`],
                      ['Waste', `${Number(selected.actual_waste_quantity||0).toLocaleString()}`],
                      ['Verified Qty', selected.inventory_verified_quantity ? Number(selected.inventory_verified_quantity).toLocaleString() : '—'],
                      ['Verification difference', selected.inventory_variance != null ? (selected.inventory_variance > 0 ? '+':'')+Number(selected.inventory_variance).toLocaleString() : '—'],
                      ['Verification difference reason', selected.variance_reason||'—'],
                    ].map(([l,v])=>(
                      <div key={l}>
                        <div style={{fontSize:10,color:'#8A8A8A',marginBottom:2}}>{l}</div>
                        <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status history */}
              {selected.history?.length > 0 && (
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:'#3B2A21',textTransform:'uppercase',
                    letterSpacing:'.06em',marginBottom:8}}>Status History</div>
                  {selected.history.map((h,i) => (
                    <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',
                      padding:'6px 0',borderBottom:'1px solid #F2EFE7'}}>
                      <div style={{fontSize:10,color:'#8A8A8A',whiteSpace:'nowrap',minWidth:120}}>
                        {new Date(h.created_at).toLocaleString('en-NG',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                      </div>
                      <div style={{flex:1}}>
                        <span style={{fontSize:11,fontWeight:600,color:'#3B2A21'}}>{h.changed_by_name}</span>
                        <span style={{fontSize:11,color:'#8A8A8A'}}> → {STATUS_COLORS[h.to_status]?.label||h.to_status}</span>
                        {h.notes && <div style={{fontSize:11,color:'#555',marginTop:2}}>{h.notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create MCR Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-hdr">
              <span className="modal-title">New Material Conversion Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-group">
                <label className="form-label">Conversion Rule (optional — auto-fills fields)</label>
                <select className="form-control" value={modal.conversion_rule_id||''}
                  onChange={e=>onRuleSelect(e.target.value)}>
                  <option value="">Select rule or fill manually...</option>
                  {rules.map(r=>(
                    <option key={r.id} value={r.id}>
                      {r.source_description} → {r.dest_description} ({r.expected_yield} {r.dest_unit} per {r.source_unit})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Parent Material (Raw Stock) *</label>
                  <select className="form-control" value={modal.source_material_id||''}
                    onChange={e=>setModal({
                      ...modal,
                      source_material_id:
                        e.target.value,
                      destination_sku_id:''
                    })}>
                    <option value="">Select parent material...</option>
                    {activeMaterials.map(s=>(
                      <option key={s.id} value={s.id}>
                        {s.material_code} — {s.name} (Available: {Number(s.current_stock||0).toLocaleString()} {s.unit_of_measure})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Destination SKU (Prepared) *</label>
                  <select className="form-control" value={modal.destination_sku_id||''}
                    onChange={e=>setModal({...modal,destination_sku_id:e.target.value})}>
                    <option value="">Select destination child SKU...</option>
                    {destinationSKUs.map(s=>(
                      <option key={s.id} value={s.id}>
                        {s.sku} — {s.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Raw Quantity to Issue *</label>
                  <input type="number" className="form-control" value={modal.raw_quantity_to_issue||''}
                    onChange={e=>onQtyChange(e.target.value)}
                    placeholder="e.g. 5" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Expected Output Quantity</label>
                  <input type="number" className="form-control" readOnly={Boolean(rules.find(r=>r.id===modal.conversion_rule_id)?.cutting_config)} value={modal.expected_output_quantity??''}
                    onChange={e=>setModal({...modal,expected_output_quantity:e.target.value})}
                    placeholder="Auto-calculated from rule" min="0" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Expected Waste</label>
                  <input type="number" className="form-control" readOnly={Boolean(rules.find(r=>r.id===modal.conversion_rule_id)?.cutting_config)} value={modal.expected_waste_quantity||0}
                    onChange={e=>setModal({...modal,expected_waste_quantity:e.target.value})}
                    min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Required Completion Date</label>
                  <input type="date" className="form-control" value={modal.required_completion_date||''}
                    onChange={e=>setModal({...modal,required_completion_date:e.target.value})} />
                </div>
              </div>

              <CuttingLayout plan={previewCut(rules.find(r=>r.id===modal.conversion_rule_id),modal.raw_quantity_to_issue)} />
              <div className="form-group">
                <label className="form-label">Cut Specification</label>
                <input className="form-control" value={modal.cut_specification||''}
                  onChange={e=>setModal({...modal,cut_specification:e.target.value})}
                  placeholder="e.g. 26 inches per piece" />
              </div>

              <div className="form-group">
                <label className="form-label">Related Job Order</label>
                <select className="form-control" value={modal.related_job_order_id||''}
                  onChange={e=>setModal({...modal,related_job_order_id:e.target.value})}>
                  <option value="">None / Stock replenishment</option>
                  {jobOrders
                  .filter(jo =>
                    [
                      'pending',
                      'ready'
                    ].includes(jo.status)
                  )
                  .map(jo=>(
                    <option key={jo.id} value={jo.id}>
                      {jo.job_number} — {jo.customer_name||''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Reason *</label>
                <select className="form-control" value={modal.reason||''}
                  onChange={e=>setModal({...modal,reason:e.target.value})}>
                  <option value="">Select reason...</option>
                  <option value="Replenish prepared stock">Replenish prepared stock</option>
                  <option value="Job Order requirement">Job Order requirement</option>
                  <option value="Emergency production requirement">Emergency production requirement</option>
                  <option value="Stock preparation">Stock preparation</option>
                </select>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveMCR} disabled={saving}>
                {saving?'Saving...':'Create MCR'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal — Complete Conversion */}
      {actionModal?.type === 'complete' && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setActionModal(null)}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Complete Conversion — {actionModal.mcr.mcr_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setActionModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#555'}}>
                Record the actual results of the conversion. Inventory will physically verify quantities before stock is updated.
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Actual Raw Consumed</label>
                  <input type="number" className="form-control" value={actionModal.actual_raw_consumed??''}
                    onChange={e=>setActionModal({...actionModal,actual_raw_consumed:e.target.value})}
                    min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Usable Pieces Produced *</label>
                  <input type="number" className="form-control" value={actionModal.actual_output_quantity??''}
                    onChange={e=>setActionModal({...actionModal,actual_output_quantity:e.target.value})}
                    min="0" autoFocus />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Rejected Cut Pieces (pcs)</label>
                  <input type="number" className="form-control" value={actionModal.actual_waste_quantity||0}
                    onChange={e=>setActionModal({...actionModal,actual_waste_quantity:e.target.value})}
                    min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label className="form-label">Damaged Pieces</label>
                  <input type="number" className="form-control" value={actionModal.damaged_quantity||0}
                    onChange={e=>setActionModal({...actionModal,damaged_quantity:e.target.value})}
                    min="0" />
                </div>
              </div>

              <div style={{padding:12,background:'#F2EFE7',marginBottom:12}}><strong>Planned good pieces: {Number(actionModal.mcr.expected_output_quantity||0)}</strong><p>Actual good pieces: {Number(actionModal.actual_output_quantity||0)}; variance: {Number(actionModal.actual_output_quantity||0)-Number(actionModal.mcr.expected_output_quantity||0)}. Explain any difference in production notes.</p></div>
              {actionModal.mcr.cutting_config?.type==='benchmark' && <div style={{padding:12,background:'#F2EFE7',marginBottom:12}}><h4>Ribbon raw reconciliation</h4><p>Cut each ribbon strip to 24 inches. Ten strips per roll is a planning benchmark. Enter the actual number cut. Record consumed rolls, unused rolls returned and raw waste separately; no guessed ribbon dimensions are required.</p><label>Raw waste within consumed rolls<input type="number" min="0" step="0.001" className="form-control" value={actionModal.raw_waste_quantity||0} onChange={e=>setActionModal({...actionModal,raw_waste_quantity:e.target.value,raw_reconciliation_confirmed:false})}/></label><label><input type="checkbox" checked={Boolean(actionModal.raw_reconciliation_confirmed)} onChange={e=>setActionModal({...actionModal,raw_reconciliation_confirmed:e.target.checked})}/> I reconciled consumed ribbon, returns and raw waste and counted actual good, damaged and rejected lengths.</label></div>}
              {actionModal.mcr.cutting_config && actionModal.mcr.cutting_config.type!=='benchmark' && <div style={{padding:12,background:'#F2EFE7',marginBottom:12}}>
                <h4>Reusable offcuts</h4>
                <p>Enter measured offcuts separately. They are recorded after Inventory verification and are not added back as full-width yards. For webbing and zipper tape use width 1 and the actual leftover length in inches.</p>
                {(actionModal.cutting_offcuts||[]).map((o,index)=><div key={index} style={{borderBottom:'1px solid #ccc',paddingBottom:8}}>
                  {[['Width (inches)','width_inches'],['Length (inches)','length_inches'],['Number of offcuts','quantity']].map(([label,key])=><label key={key} style={{display:'inline-block',maxWidth:130,marginRight:6}}>{label}<input type="number" min="0" step={key==='quantity'?'1':'0.01'} className="form-control" value={o[key]} onChange={e=>setActionModal({...actionModal,raw_reconciliation_confirmed:false,cutting_offcuts:actionModal.cutting_offcuts.map((v,j)=>j===index?{...v,[key]:e.target.value}:v)})}/></label>)}
                  <button className="btn btn-ghost btn-sm" onClick={()=>setActionModal({...actionModal,raw_reconciliation_confirmed:false,cutting_offcuts:actionModal.cutting_offcuts.filter((_,j)=>j!==index)})}>Remove</button>
                </div>)}
                <button className="btn btn-outline btn-sm" onClick={()=>setActionModal({...actionModal,raw_reconciliation_confirmed:false,cutting_offcuts:[...(actionModal.cutting_offcuts||[]),{width_inches:actionModal.mcr.cutting_config.type==='linear'?1:'',length_inches:'',quantity:1}]})}>Add measured offcut</button>
                <p><strong>Raw waste: {rawWasteFor(actionModal.mcr,actionModal)} {actionModal.mcr.cutting_config.source_unit}</strong> — consumed material less all cut pieces and reusable offcuts. A negative figure must be corrected.</p>
                <label><input type="checkbox" checked={Boolean(actionModal.raw_reconciliation_confirmed)} onChange={e=>setActionModal({...actionModal,raw_reconciliation_confirmed:e.target.checked})}/> I have recorded reusable offcuts and confirm the remaining raw material is waste.</label>
              </div>}
              <div className="form-group">
                <label className="form-label">Remaining Raw Material Returned</label>
                <input type="number" className="form-control" value={actionModal.remaining_raw_returned||0}
                  onChange={e=>setActionModal({...actionModal,remaining_raw_returned:e.target.value})}
                  min="0" step="0.01" />
              </div>

              <div className="form-group">
                <label className="form-label">Production notes / yield variance explanation</label>
                <textarea className="form-control" rows={2} value={actionModal.production_notes||''}
                  onChange={e=>setActionModal({...actionModal,production_notes:e.target.value})}
                  placeholder="Any notes about the conversion..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setActionModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={completeConversion} disabled={saving}
                style={{background:'#556B2F',border:'none'}}>
                {saving?'Saving...':'Return to Inventory for Verification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal — Verify Conversion */}
      {actionModal?.type === 'verify' && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setActionModal(null)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">Verify Conversion — {actionModal.mcr.mcr_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setActionModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
                padding:12,marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
                ⚠ <strong>Physical count required.</strong> Count the returned pieces yourself
                before entering the verified quantity. Do not rely on what Production reported.
              </div>

              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16}}>
                <div style={{fontSize:12,color:'#555'}}>
                  Production reported: <strong>{Number(actionModal.mcr.actual_output_quantity||0).toLocaleString()}</strong> pieces
                </div>
                <div style={{fontSize:12,color:'#555',marginTop:4}}>
                  Expected output: <strong>{Number(actionModal.mcr.expected_output_quantity||0).toLocaleString()}</strong> pieces
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Inventory Verified Quantity (physical count) *</label>
                <input type="number" className="form-control" value={actionModal.inventory_verified_quantity||''}
                  onChange={e=>setActionModal({...actionModal,inventory_verified_quantity:e.target.value})}
                  min="0" autoFocus />
              </div>

              {actionModal.inventory_verified_quantity && (
                (() => {
                  const v = parseFloat(actionModal.inventory_verified_quantity);
                  const e = parseFloat(actionModal.mcr.actual_output_quantity ?? actionModal.mcr.expected_output_quantity);
                  const diff = v - e;
                  return diff !== 0 ? (
                    <div style={{background:diff<0?'#FDEAEA':'#FFF3DC',borderRadius:8,
                      padding:'10px 14px',marginBottom:14,fontSize:12,
                      color:diff<0?'#8F2E24':'#8B5E0A',fontWeight:600}}>
                      {diff<0?`⚠ Shortage: ${Math.abs(diff).toLocaleString()} pieces less than reported`
                             :`⚠ Excess: ${diff.toLocaleString()} pieces more than reported`}
                    </div>
                  ) : (
                    <div style={{background:'#E8F5EB',borderRadius:8,padding:'10px 14px',
                      marginBottom:14,fontSize:12,color:'#2E6B3A',fontWeight:600}}>
                      ✅ Quantity matches production report
                    </div>
                  );
                })()
              )}

              {parseFloat(actionModal.inventory_verified_quantity||0) !==
               parseFloat(actionModal.mcr.actual_output_quantity ?? actionModal.mcr.expected_output_quantity) && (
                <div className="form-group">
                  <label className="form-label">Variance Reason *</label>
                  <textarea className="form-control" rows={2} value={actionModal.variance_reason||''}
                    onChange={e=>setActionModal({...actionModal,variance_reason:e.target.value})}
                    placeholder="Explain the difference between reported and verified quantity..." />
                </div>
              )}

              <div style={{background:'#FDEAEA',borderRadius:8,padding:12,fontSize:12,color:'#8F2E24'}}>
                ⚠ Confirming verification will <strong>permanently update stock balances</strong> —
                raw material was deducted at acceptance; verified prepared pieces will now be added, and unused raw material will be returned.
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setActionModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={verifyConversion} disabled={saving}
                style={{background:'#2E6B3A',border:'none'}}>
                {saving?'Verifying...':'✅ Confirm & Update Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
